import React, { Fragment, useEffect, useState, type FC, type PropsWithChildren } from 'react';

import { Heading, Box } from '@lucid/components';

import type { Lucid } from 'types/types';
import Person, { type PersonColor, type PersonGeneric } from './person';
import BioModal from './bio-modal';

export interface PeopleSectionProps extends Omit<Lucid.Directus.Generated.People_Section, 'people' | 'id'> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Set to show the company name - otherwise hide it */
  willShowCompany?: boolean;
  /** Sets the color scheme for the people backgrounds */
  colorScheme?: PersonColor[];
  /** The available variants for the PeopleSection component. */
  variant?: Lucid.Theme.Component.PeopleSection.VariantKeys;
  /** Set to show a dark background */
  isDarkBackground?: boolean;
  /** Set to hide the bio */
  shouldHideBio?: boolean;
  /** The normalized people data to be rendered in the Person component */
  people: PersonGeneric[];
  /** The current state of the modal - used to override modal behavior when user clicks nested jumplinks  */
  isModalOpen?: boolean;
  /** Used to override modal behavior when user clicks nested jumplinks within the modal */
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const normalizePople = (people?: Lucid.Directus.Generated.People_Section_Person[]) =>
  people?.map(({ person_id: person }) => ({
    ...person,
    name: person?.name,
    image: person?.image,
    company: person?.company,
    bio: person?.translations?.[0]?.bio,
    title: person?.translations?.[0]?.title,
  })) as PersonGeneric[];

const PeopleSection: FC<PropsWithChildren<PeopleSectionProps>> = ({
  className,
  heading,
  isDarkBackground,
  people,
  willShowCompany = false,
  shouldHideBio = false,
  isModalOpen,
  setIsModalOpen,
}) => {
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [currentPerson, setCurrentPerson] = useState<PersonGeneric | null>(null);

  useEffect(() => {
    if (expandedIndex === -1 || !people) return setCurrentPerson(null);

    setCurrentPerson(people?.[expandedIndex]);
    if (setIsModalOpen) setIsModalOpen(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedIndex]);

  return (
    <Box className={className}>
      <Box
        css={theme => ({
          margin: 'auto',
          marginBottom: theme.space.s64,
        })}
      >
        {heading && (
          <Heading
            as="h2"
            css={theme => ({ color: isDarkBackground ? theme.colors.white : theme.colors.coolGrey[100] })}
            variant="heading2XL"
          >
            {heading}
          </Heading>
        )}
      </Box>
      <ul
        css={theme => ({
          display: 'grid',
          listStyle: 'none',
          justifyContent: 'center',
          width: '100%',
          marginLeft: 0,
          gridTemplateColumns: 'repeat(1, 1fr)',
          columnGap: theme.space.s24,
          [theme.breakpoints.bp500.min]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
          },
          [theme.breakpoints.bp900.min]: {
            gridTemplateColumns: 'repeat(4, 1fr)',
          },
        })}
      >
        {people?.map(
          (person, index) =>
            person && (
              <Fragment key={`person-${index}`}>
                <li
                  key={`person-${index}`}
                  css={theme => ({
                    margin: 0,
                    marginBottom: theme.space.s64,
                    gridColumn: '1 / 2',
                    gridRow: `${Math.floor(index) * 2 + 1} / ${Math.floor(index) * 2 + 2}`,
                    [theme.breakpoints.bp500.min]: {
                      gridColumn: `${(index % 2) + 1} / ${(index % 2) + 2}`,
                      gridRow: `${Math.floor(index / 2) * 2 + 1} / ${Math.floor(index / 2) * 2 + 2}`,
                    },
                    [theme.breakpoints.bp900.min]: {
                      gridColumn: `${(index % 4) + 1} / ${(index % 4) + 2}`,
                      gridRow: `${Math.floor(index / 4) * 2 + 1} / ${Math.floor(index / 4) * 2 + 2}`,
                    },
                  })}
                >
                  <Person
                    hasVisibleBioLink
                    expandedIndex={expandedIndex}
                    index={index}
                    isDarkBackground={isDarkBackground}
                    person={person}
                    setExpandedIndex={setExpandedIndex}
                    shouldHideBio={shouldHideBio}
                    willShowCompany={willShowCompany}
                  />
                </li>
                {expandedIndex === index && currentPerson && (currentPerson.bio || currentPerson.bioDetails) && (
                  <BioModal
                    handleCloseModal={() => setExpandedIndex(-1)}
                    isModalOpen={isModalOpen}
                    person={currentPerson}
                  />
                )}
              </Fragment>
            ),
        )}
      </ul>
    </Box>
  );
};

export { PeopleSection };
export default PeopleSection;
